import { useState } from "react";
import { X, ExternalLink, Play, BookOpen, Pin } from "lucide-react";
import { toast } from "../components/ui/sonner";

export const DetailModal = ({ isOpen, onClose, type, data, userProfile }) => {
  if (!isOpen || !data) return null;

  const renderContent = () => {
    switch (type) {
      case "pulse":
        return (
          <div className="space-y-4">
            <h2 className="font-headline text-xl font-medium text-[#F8FAFC]">{data.headline}</h2>
            {data.metadata?.content_origin === "real" && (
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#14B8A6]/10 text-[#14B8A6] text-xs">
                <span className="w-2 h-2 rounded-full bg-[#14B8A6] animate-pulse" />
                Live Content
              </span>
            )}
            <div className="space-y-4">
              <div>
                <h4 className="text-label-md text-[#94A3B8] mb-1">What Happened</h4>
                <p className="text-sm text-[#F8FAFC]/80">{data.what_happened || data.summary}</p>
              </div>
              <div>
                <h4 className="text-label-md text-[#94A3B8] mb-1">Why It Matters</h4>
                <p className="text-sm text-[#F8FAFC]/80">{data.why_it_matters}</p>
              </div>
              <div>
                <h4 className="text-label-md text-[#94A3B8] mb-1">Actionable Takeaway</h4>
                <p className="text-sm text-[#F8FAFC]/80">{data.actionable_takeaway}</p>
              </div>
              {userProfile === "leader" && data.leader_variant && (
                <div className="p-4 rounded-lg bg-[#6D28D9]/[0.06] border-l-2 border-[#6D28D9]">
                  <h4 className="text-xs text-[#A78BFA] font-medium mb-1">Leader Insight</h4>
                  <p className="text-sm text-[#F8FAFC]/80">{data.leader_variant}</p>
                </div>
              )}
              {userProfile === "user" && data.user_variant && (
                <div className="p-4 rounded-lg bg-[#14B8A6]/[0.06] border-l-2 border-[#14B8A6]">
                  <h4 className="text-xs text-[#14B8A6] font-medium mb-1">For Your Role</h4>
                  <p className="text-sm text-[#F8FAFC]/80">{data.user_variant}</p>
                </div>
              )}
            </div>
            {data.metadata?.source?.url && (
              <a href={data.metadata.source.url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#14B8A6] text-[#050B14] text-sm font-medium hover:bg-[#0D9488] transition-colors">
                Read Full Article <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        );

      case "learning":
        return (
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <h2 className="font-headline text-xl font-medium text-[#F8FAFC]">{data.title}</h2>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                data.level === "beginner" ? "bg-[#14B8A6]/10 text-[#14B8A6]" :
                data.level === "intermediate" ? "bg-[#6D28D9]/10 text-[#A78BFA]" :
                "bg-amber-500/10 text-amber-400"
              }`}>{data.level}</span>
            </div>
            <p className="text-sm text-[#94A3B8]">{data.summary}</p>
            <div className="flex items-center gap-4 text-xs text-[#94A3B8]">
              <span>Duration: {data.estimated_duration}</span>
              <span>Progress: {data.progress_percent}%</span>
            </div>
            <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden">
              <div className="h-full progress-gradient rounded-full transition-all" style={{ width: `${data.progress_percent}%` }} />
            </div>
            {data.steps && data.steps.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-[#F8FAFC]">Modules</h4>
                {data.steps.map((step, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${
                    idx < Math.floor(data.progress_percent / 100 * data.steps.length)
                      ? 'bg-[#14B8A6]/[0.04] border-[#14B8A6]/20'
                      : 'bg-white/[0.02] border-white/[0.06]'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                        idx < Math.floor(data.progress_percent / 100 * data.steps.length)
                          ? 'bg-[#14B8A6] text-[#050B14]'
                          : 'bg-white/[0.06] text-[#94A3B8]'
                      }`}>
                        {idx < Math.floor(data.progress_percent / 100 * data.steps.length) ? (
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
                        ) : idx + 1}
                      </div>
                      <span className="text-sm text-[#F8FAFC]">{step.step_title}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {data.next_step_recommendation && (
              <div className="p-3 rounded-lg bg-[#6D28D9]/[0.06]">
                <h4 className="text-xs text-[#A78BFA] font-medium mb-1">Next Step</h4>
                <p className="text-sm text-[#F8FAFC]/80">{data.next_step_recommendation}</p>
              </div>
            )}
            <button 
              onClick={() => toast.success("Added to learning plan!")}
              className="w-full py-3 rounded-md bg-[#14B8A6] text-[#050B14] font-medium hover:bg-[#0D9488] transition-colors text-sm">
              {data.progress_percent > 0 ? 'Continue Learning' : 'Start Pathway'}
            </button>
          </div>
        );

      case "orgPulse":
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              {data.org_name && <span className="px-2 py-1 rounded-full bg-[#14B8A6]/10 text-[#14B8A6] text-xs font-medium">{data.org_name}</span>}
              <span className="px-2 py-1 rounded-full bg-[#6D28D9]/10 text-[#A78BFA] text-xs capitalize">{data.update_type}</span>
            </div>
            <h2 className="font-headline text-xl font-medium text-[#F8FAFC]">{data.headline}</h2>
            <p className="text-sm text-[#94A3B8]">{data.summary}</p>
            <div className="p-3 rounded-lg bg-amber-500/[0.06] border-l-2 border-amber-400">
              <h4 className="text-xs text-amber-400 font-medium mb-1">Why It Matters</h4>
              <p className="text-sm text-[#F8FAFC]/80">{data.why_it_matters}</p>
            </div>
            {data.strategic_implication && (
              <div className="p-3 rounded-lg bg-[#6D28D9]/[0.06] border-l-2 border-[#6D28D9]">
                <h4 className="text-xs text-[#A78BFA] font-medium mb-1">Strategic Implication</h4>
                <p className="text-sm text-[#F8FAFC]/80">{data.strategic_implication}</p>
              </div>
            )}
            {data.source_url && (
              <a href={data.source_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#14B8A6] text-[#050B14] text-sm font-medium hover:bg-[#0D9488] transition-colors">
                View Source <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        );

      case "video":
        return (
          <div className="space-y-4">
            {/* Video Player Area */}
            <a
              href={data.video_url || data.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block aspect-video bg-[#050B14] rounded-xl relative overflow-hidden group cursor-pointer"
            >
              <img
                src={data.thumbnail_url || "https://images.pexels.com/photos/1181433/pexels-photo-1181433.jpeg"}
                alt={data.title}
                className="w-full h-full object-cover opacity-60 group-hover:opacity-70 transition-opacity"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-[#14B8A6]/90 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-[#14B8A6]/20">
                  <Play className="w-7 h-7 text-[#050B14] ml-1" fill="currentColor" />
                </div>
              </div>
              <span className="absolute bottom-3 right-3 flex items-center gap-1 text-xs text-white bg-black/60 px-2 py-1 rounded">
                {data.duration}
              </span>
            </a>
            <h2 className="font-headline text-xl font-medium text-[#F8FAFC]">{data.title}</h2>
            {data.channel_name && (
              <p className="text-xs text-[#94A3B8]">{data.channel_name}</p>
            )}
            <p className="text-sm text-[#94A3B8]">{data.summary}</p>
            {data.key_insights && data.key_insights.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-[#F8FAFC]">Key Insights</h4>
                <ul className="space-y-2">
                  {data.key_insights.map((insight, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <BookOpen className="w-4 h-4 text-[#14B8A6] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#F8FAFC]/80">{insight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(data.video_url || data.source_url) && (
              <a
                href={data.video_url || data.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 rounded-md bg-[#14B8A6] text-[#050B14] font-medium hover:bg-[#0D9488] transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <Play className="w-4 h-4" fill="currentColor" /> Watch on YouTube
              </a>
            )}
          </div>
        );

      case "knowledge":
        return (
          <div className="space-y-4">
            <span className="inline-block px-2 py-1 rounded-full bg-[#6D28D9]/10 text-[#A78BFA] text-xs capitalize">{data.category}</span>
            <h2 className="font-headline text-xl font-medium text-[#F8FAFC]">{data.title}</h2>
            <p className="text-sm text-[#F8FAFC]/80">{data.content_body}</p>
            {data.corporate_impact_statement && (
              <div className="p-3 rounded-lg bg-amber-500/[0.06] border-l-2 border-amber-400">
                <h4 className="text-xs text-amber-400 font-medium mb-1">Corporate Impact</h4>
                <p className="text-sm text-[#F8FAFC]/80">{data.corporate_impact_statement}</p>
              </div>
            )}
            {data.topic_tags && data.topic_tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {data.topic_tags.map((tag, idx) => (
                  <span key={idx} className="px-2 py-1 rounded-full bg-white/[0.04] text-[#94A3B8] text-xs">{tag}</span>
                ))}
              </div>
            )}
            <button
              onClick={() => toast.success("Added to Learning Plan!")}
              className="w-full py-3 rounded-md bg-[#14B8A6] text-[#050B14] font-medium hover:bg-[#0D9488] transition-colors text-sm">
              Add to Learning Plan
            </button>
          </div>
        );

      default:
        return <p className="text-[#94A3B8]">No content available</p>;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="detail-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0A192F] border border-white/10 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-fade-in">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/[0.06] transition-colors z-10" data-testid="modal-close">
          <X className="w-5 h-5 text-[#94A3B8]" />
        </button>
        <div className="p-6 pt-12">{renderContent()}</div>
      </div>
    </div>
  );
};

export const AMAManageModal = ({ isOpen, onClose, clusters, questions, onSummarize, onRespond, onPin }) => {
  const [activeCluster, setActiveCluster] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [summaryResult, setSummaryResult] = useState(null);

  if (!isOpen) return null;

  const handleSummarize = async (clusterId) => {
    setIsSubmitting(true);
    try {
      const result = await onSummarize(clusterId);
      setSummaryResult(result);
    } catch { /* ignore */ } finally { setIsSubmitting(false); }
  };

  const handleRespond = async () => {
    if (!activeCluster || !responseText.trim()) return;
    setIsSubmitting(true);
    try {
      await onRespond(activeCluster.cluster_id, responseText);
      setResponseText("");
      setActiveCluster(null);
      toast.success("Response submitted!");
    } catch { /* ignore */ } finally { setIsSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ama-manage-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0A192F] border border-white/10 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fade-in">
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <h2 className="font-headline text-lg font-medium text-[#F8FAFC]">Manage AMA Questions</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/[0.06]"><X className="w-5 h-5 text-[#94A3B8]" /></button>
        </div>
        
        <div className="flex h-[70vh]">
          {/* Clusters */}
          <div className="w-1/3 border-r border-white/[0.06] overflow-y-auto p-4">
            <h3 className="text-label-md text-[#94A3B8] mb-3">Question Clusters</h3>
            {clusters.map((cluster) => (
              <div key={cluster.cluster_id} onClick={() => setActiveCluster(cluster)}
                className={`p-3 rounded-lg mb-2 cursor-pointer transition-colors ${
                  activeCluster?.cluster_id === cluster.cluster_id
                    ? 'bg-[#14B8A6]/10 border border-[#14B8A6]/20'
                    : 'bg-white/[0.02] hover:bg-white/[0.04] border border-transparent'
                }`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-[#14B8A6] capitalize">{cluster.theme}</span>
                  <span className="text-xs text-[#94A3B8]">{cluster.question_ids?.length || 0} Qs</span>
                </div>
                <p className="text-sm font-medium text-[#F8FAFC] line-clamp-2">{cluster.summary}</p>
              </div>
            ))}
          </div>
          
          {/* Detail */}
          <div className="flex-1 overflow-y-auto p-4">
            {activeCluster ? (
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-xs font-medium text-[#14B8A6] capitalize">{activeCluster.theme}</span>
                    <h3 className="text-lg font-medium text-[#F8FAFC]">{activeCluster.summary}</h3>
                  </div>
                  <button onClick={() => handleSummarize(activeCluster.cluster_id)} disabled={isSubmitting}
                    className="px-3 py-1.5 rounded-md bg-[#14B8A6]/10 text-[#14B8A6] text-sm font-medium hover:bg-[#14B8A6]/20 disabled:opacity-50">
                    {isSubmitting ? 'Generating...' : 'AI Summarize'}
                  </button>
                </div>
                
                {summaryResult && summaryResult.cluster_id === activeCluster.cluster_id && (
                  <div className="p-4 rounded-lg bg-[#14B8A6]/[0.06] border border-[#14B8A6]/20">
                    <h4 className="text-xs text-[#14B8A6] font-medium mb-2">AI Summary</h4>
                    <p className="text-sm text-[#F8FAFC]/80">{summaryResult.summary}</p>
                  </div>
                )}
                
                <div>
                  <h4 className="text-label-md text-[#94A3B8] mb-2">Questions</h4>
                  {questions.filter(q => activeCluster.question_ids?.includes(q.id)).map((q) => (
                    <div key={q.id} className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] mb-2">
                      <p className="text-sm text-[#F8FAFC]/80">"{q.submitted_text}"</p>
                      <p className="text-xs text-[#94A3B8] mt-1">{q.user_role_tag} - {new Date(q.submitted_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>

                {activeCluster.responses && activeCluster.responses.length > 0 && (
                  <div>
                    <h4 className="text-label-md text-[#94A3B8] mb-2">Responses</h4>
                    {activeCluster.responses.map((resp) => (
                      <div key={resp.response_id} className={`p-3 rounded-lg mb-2 ${resp.pinned ? 'bg-[#6D28D9]/[0.06] border border-[#6D28D9]/20' : 'bg-white/[0.02] border border-white/[0.04]'}`}>
                        <div className="flex items-center justify-between mb-1">
                          {resp.pinned && <span className="flex items-center gap-1 text-xs text-[#A78BFA] font-medium"><Pin className="w-3 h-3" /> Pinned</span>}
                          <button onClick={() => onPin(resp.response_id, !resp.pinned)} className="text-xs text-[#94A3B8] hover:text-[#14B8A6]">
                            {resp.pinned ? 'Unpin' : 'Pin'}
                          </button>
                        </div>
                        <p className="text-sm text-[#F8FAFC]/80">{resp.response_text}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="pt-4 border-t border-white/[0.06]">
                  <h4 className="text-xs text-[#94A3B8] font-medium mb-2">Add Response</h4>
                  <textarea value={responseText} onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Write your response..."
                    className="w-full h-24 p-3 rounded-lg bg-white/[0.04] border border-white/[0.06] text-[#F8FAFC] placeholder-[#94A3B8]/40 resize-none focus:ring-1 focus:ring-[#14B8A6]/50 focus:outline-none text-sm" />
                  <button onClick={handleRespond} disabled={!responseText.trim() || isSubmitting}
                    className="mt-2 px-4 py-2 rounded-md bg-[#14B8A6] text-[#050B14] text-sm font-medium hover:bg-[#0D9488] disabled:opacity-30 disabled:cursor-not-allowed">
                    {isSubmitting ? 'Submitting...' : 'Submit Response'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-[#94A3B8] text-sm">
                Select a cluster to view and manage questions
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const LearningGalleryModal = ({ isOpen, onClose, pathways, onSelectPathway }) => {
  const [filter, setFilter] = useState("all");
  if (!isOpen) return null;
  
  const filteredPathways = filter === "all" ? pathways : pathways.filter(p => p.level === filter);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="learning-gallery-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#0A192F] border border-white/10 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-fade-in">
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
          <h2 className="font-headline text-lg font-medium text-[#F8FAFC]">Learning Pathways</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/[0.06]"><X className="w-5 h-5 text-[#94A3B8]" /></button>
        </div>
        
        <div className="p-4 border-b border-white/[0.06] flex gap-2">
          {["all", "beginner", "intermediate", "advanced"].map((level) => (
            <button key={level} onClick={() => setFilter(level)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors capitalize ${
                filter === level ? 'bg-[#14B8A6]/10 text-[#14B8A6]' : 'text-[#94A3B8] hover:bg-white/[0.04]'
              }`}>{level}</button>
          ))}
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredPathways.map((pathway) => (
              <div key={pathway.id} onClick={() => onSelectPathway(pathway)}
                className="p-4 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.06] hover:border-[#14B8A6]/20 cursor-pointer transition-all">
                <div className="flex items-start justify-between mb-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    pathway.level === "beginner" ? "bg-[#14B8A6]/10 text-[#14B8A6]" :
                    pathway.level === "intermediate" ? "bg-[#6D28D9]/10 text-[#A78BFA]" :
                    "bg-amber-500/10 text-amber-400"
                  }`}>{pathway.level}</span>
                  <span className="text-xs text-[#94A3B8]">{pathway.estimated_duration}</span>
                </div>
                <h3 className="text-sm font-medium text-[#F8FAFC] mb-1">{pathway.title}</h3>
                <p className="text-xs text-[#94A3B8] line-clamp-2 mb-3">{pathway.summary}</p>
                {pathway.progress_percent > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full">
                      <div className="h-full bg-[#14B8A6] rounded-full" style={{ width: `${pathway.progress_percent}%` }} />
                    </div>
                    <span className="text-xs text-[#94A3B8]">{pathway.progress_percent}%</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
